/**
 * "Ask Resto" — which parser answers a given sentence.
 *
 * `lib/nlSearch.ts` is the floor: offline, deterministic, free, and right about
 * most of what people type. This module adds the optional upgrade — the Fly
 * server's `POST /api/ai/parse-search` — and, more importantly, decides when
 * NOT to use it:
 *
 *   1. The local parse consumed the whole sentence → done. No network call:
 *      a model can't improve on "pizza open now with a discount", and a
 *      round-trip would only add latency to the tap.
 *   2. Otherwise ask the server, with a patience budget shorter than the
 *      server's own model timeout, and use its answer only when a REAL
 *      provider produced it (`source: "llm"`). A `mock` parse is canned
 *      wiring-proof output — applying it would fill the feed with someone
 *      else's filters.
 *   3. Anything else (timeout, 429, offline, mock, empty parse) → the local
 *      result, which the visitor already deserved.
 *
 * After the first canned answer or dead round-trip the endpoint is written off
 * for the rest of the page session: with no key configured (the current
 * production state) every response is `mock`, and a second three-second wait
 * for the same silence is pure cost. A reload re-probes.
 *
 * Whatever comes back is re-validated against the client's own taxonomy before
 * it touches state — the server validates too, but a chip whose slug the feed
 * doesn't know would render as a raw slug and filter to nothing.
 */

import { parseNaturalQuery, type ParsedQuery, type Vocabulary } from "@/lib/nlSearch";
import type { FilterToken, IntentFilters } from "@/lib/feedFilter";

export interface AskResult extends ParsedQuery {
  /** Which parser produced this — "llm" only when a real provider answered. */
  source: "local" | "llm";
}

/** Shorter than the server's 4 s model budget: the tap must feel like a tap. */
const ASK_TIMEOUT_MS = 3_000;

interface ServerParse {
  ok?: unknown;
  source?: unknown;
  tokens?: unknown;
  intent?: unknown;
  text?: unknown;
}

// Session-scoped: the endpoint answered, but not with anything we can use
// (almost always "no key configured"). Stop dialing until the next reload.
let serverParseUsable = true;

/** Test hook — forget the "don't bother" flag. @public */
export function resetAskProbe(): void {
  serverParseUsable = true;
}

const INTENT_LEVELS = new Set(["GREEN", "YELLOW"]);

/** Keep only what this client's taxonomy + filter vocabulary can actually honor. */
function adoptServerParse(raw: ServerParse, query: string, vocab: Vocabulary): ParsedQuery | null {
  const cuisineSlugs = new Set(vocab.cuisines.map((c) => c.slug));
  const tagSlugs = new Set(vocab.tags.map((t) => t.slug));

  const tokens: FilterToken[] = [];
  if (Array.isArray(raw.tokens)) {
    for (const t of raw.tokens) {
      if (!t || typeof t !== "object") continue;
      const { kind, slug } = t as { kind?: unknown; slug?: unknown };
      if (typeof slug !== "string") continue;
      if (kind === "cuisine" && cuisineSlugs.has(slug)) tokens.push({ kind, slug });
      else if (kind === "tag" && tagSlugs.has(slug)) tokens.push({ kind, slug });
    }
  }

  const intent: IntentFilters = {};
  const src = (raw.intent ?? {}) as Record<string, unknown>;
  if (src.openNow === true) intent.openNow = true;
  if (src.hasDiscount === true) intent.hasDiscount = true;
  if (src.fastVenue === true) intent.fastVenue = true;
  if (typeof src.capacityMax === "string" && INTENT_LEVELS.has(src.capacityMax)) {
    intent.capacityMax = src.capacityMax as IntentFilters["capacityMax"];
  }
  if (typeof src.maxDistanceKm === "number" && Number.isFinite(src.maxDistanceKm) && src.maxDistanceKm > 0) {
    intent.maxDistanceKm = src.maxDistanceKm;
  }

  if (tokens.length === 0 && Object.keys(intent).length === 0) return null;
  // Leftover text is advisory; the server only ever echoes words from the
  // query, but re-clamp to the typed text so a bug can't invent a filter.
  const text = typeof raw.text === "string" && query.toLowerCase().includes(raw.text.toLowerCase())
    ? raw.text
    : "";
  return { tokens, intent, text, matched: true };
}

/**
 * Parse a query into filters. Always resolves — never throws, never leaves the
 * caller without an answer.
 */
export async function askResto(
  query: string,
  vocab: Vocabulary,
  { offline = false }: { offline?: boolean } = {},
): Promise<AskResult> {
  const local = parseNaturalQuery(query, vocab);
  // Fully understood offline → the network can only make this slower.
  if (local.matched && local.text === "") return { ...local, source: "local" };
  // `offline`: the server-free `/showcase-cluj` demo never dials the LLM.
  if (offline || !serverParseUsable) return { ...local, source: "local" };

  try {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), ASK_TIMEOUT_MS);
    let res: Response;
    try {
      res = await fetch("/api/ai/parse-search", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ query }),
        signal: ctl.signal,
      });
    } finally {
      clearTimeout(timer);
    }
    if (!res.ok) {
      // 429 is transient (own small budget, refills in a minute) — everything
      // else means this build's server doesn't serve the endpoint at all (e.g.
      // deployed before the Fly release landed). Stop asking.
      if (res.status !== 429) serverParseUsable = false;
      return { ...local, source: "local" };
    }
    const body = (await res.json()) as ServerParse;
    if (body.ok !== true || body.source !== "llm") {
      // Canned (mock) or empty — the endpoint is alive but has nothing better.
      if (body.source === "mock") serverParseUsable = false;
      return { ...local, source: "local" };
    }
    const adopted = adoptServerParse(body, query, vocab);
    return adopted ? { ...adopted, source: "llm" } : { ...local, source: "local" };
  } catch {
    // Offline, timed out, malformed JSON — the local parse is the answer, and
    // we stop dialing: a second 3 s wait for the same silence is pure cost.
    serverParseUsable = false;
    return { ...local, source: "local" };
  }
}
